
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Calendar, BarChart2, MessageSquare } from "lucide-react";
import { useSubscriptionData } from "@/hooks/useSubscriptionData";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import type { Database } from "@/integrations/supabase/types";

type Client = Database["public"]["Tables"]["clients"]["Row"];

interface ClientCardProps {
  client: Client;
  onClick: () => void;
}

export function ClientCard({ client, onClick }: ClientCardProps) {
  const { data: subscription } = useSubscriptionData();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch the latest tier prompt for the user's subscription level
  const { data: latestTierPrompt } = useQuery({
    queryKey: ["tier_prompts", subscription?.tier],
    queryFn: async () => {
      if (!subscription?.tier) return null;

      const { data, error } = await supabase
        .from("tier_prompts")
        .select("*")
        .eq("subscription_tier", subscription.tier)
        .eq("active", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error("Error fetching tier prompt:", error);
        return null;
      }

      return data;
    },
    enabled: !!subscription?.tier,
  });

  const handleGenerateMessage = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click event

    if (!latestTierPrompt) {
      toast({
        title: "No prompt available",
        description: "Please contact support if this issue persists.",
        variant: "destructive",
      });
      return;
    }

    // Navigate to generate content page with client and prompt info
    navigate("/generated-content", {
      state: {
        clientId: client.id,
        promptId: latestTierPrompt.id,
      },
    });
  };

  return (
    <Card 
      className="cursor-pointer transition-all hover:shadow-md"
      onClick={onClick}
    >
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{client.client_name}</span>
          <BarChart2 className="h-5 w-5 text-muted-foreground" />
        </CardTitle>
        <div className="text-sm text-muted-foreground">{client.industry}</div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>Last Contact: {format(new Date(client.last_contact_date), 'PP')}</span>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          variant="secondary" 
          size="sm" 
          className="w-full gap-2"
          onClick={handleGenerateMessage}
          disabled={!latestTierPrompt || !subscription}
        >
          <MessageSquare className="h-4 w-4" />
          Generate Message
        </Button>
      </CardFooter>
    </Card>
  );
}
